import { useState } from 'react'
import { Head } from '@inertiajs/react'
import Navbar from '@/components/landing/Navbar'
import type { LandingLabels, Pricing } from './types'
import Hero from '@/components/landing/Hero'
import ProblemSection from '@/components/landing/ProblemSection'
import LossCalculator from '@/components/landing/LossCalculator'
import HowItWorks from '@/components/landing/HowItWorks'
import FinalCta from '@/components/landing/FinalCta'
import Footer from '@/components/landing/Footer'

// FeaturesSection, ComparisonSection, RoiCalculator, PricingSection, Testimonial, FaqSection
// and AboutSection are no longer mounted (spec 2026-07-29): the page took over five minutes
// to read, and those blocks advertised features that aren't built yet. The components and
// their locale keys are deliberately kept: they can feed a second page the day there is
// enough traffic to segment.
type Props = {
  labels: LandingLabels
  // Still sent by the controller, currently unread: PricingSection is the only consumer and
  // it is unmounted. Kept so putting the block back is a one-line change.
  pricing: Pricing
  keg_value: number
}

export default function LandingShow({ labels, keg_value }: Props) {
  // Lifted so the calculator's slider can pre-fill the lead form below it: the visitor
  // states their fleet size once, and we learn the size distribution of our waiting list.
  const [kegCount, setKegCount] = useState(50)
  const [calculatorUsed, setCalculatorUsed] = useState(false)

  function handleKegCountChange(value: number) {
    setCalculatorUsed(true)
    setKegCount(value)
  }

  // Which magnet actually converted: the calculator, or the plain form. Still spelled
  // "roi_calculator" although the calculator now shows losses rather than a return: the
  // leads already collected under that name stay comparable with the ones to come.
  const source = calculatorUsed ? 'roi_calculator' : 'landing'

  return (
    <>
      <Head title="Kego Fûté — Zéro fût perdu" />

      <div className="min-h-screen bg-paper font-body text-ink">
        <Navbar labels={labels.navbar} />
        <Hero labels={labels.hero} microcopy={labels.pricing.microcopy} />
        <ProblemSection labels={labels.problem} />
        <LossCalculator
          labels={labels.loss}
          kegValue={keg_value}
          kegCount={kegCount}
          onKegCountChange={handleKegCountChange}
        />
        <HowItWorks labels={labels.how} />
        {/* No microcopy here: the form's own disclaimer already states the limited beta two
            lines above, and saying it twice in one block was the kind of padding this page
            is being trimmed of. */}
        <FinalCta
          labels={labels.final_cta}
          leadForm={labels.lead_form}
          kegCountHint={calculatorUsed ? kegCount : undefined}
          source={source}
        />
        <Footer labels={labels.footer} />
      </div>
    </>
  )
}
